import { adapter } from '@seek/tealium-adapter';
import React from 'react';

import type { Country } from '../entities/Country';
import { getExperimentsFromFeatures } from '../entities/Feature';
import type {
  PaymentMethodType,
  UseCase,
  VirtualAccountBankAbbr,
} from '../entities/enums';
import type { TrackingAdapter, TealiumAdapter, Invoice } from '../types';
import type { AlertViewedPageData } from '../utils/analytics/types';

const AnalyticsContext = React.createContext<AnalyticsContextValue>({
  sendViewAlertViewedEvent: () => {},
  sendViewExperimentsEvent: () => {},
  sendLinkSelectPaymentMethodEvent: () => {},
  sendLinkPostMyAdButtonEvent: () => {},
  sendLinkPostCajaAdOrUpgradeButtonEvent: () => {},
  sendLinkSaveCardCheckboxEvent: () => {},
  sendLinkReplaceCardEvent: () => {},
  sendLinkDifferentPaymentMethodEvent: () => {},
  sendLinkPaymentProcessingEvent: () => {},
  sendLinkCheckPaymentEvent: () => {},
  sendLinkDeleteCardButtonEvent: () => {},
  sendLinkSelectWayToPayEvent: () => {},
  sendLinkQRDisplayEvent: () => {},
});

interface AnalyticsContextValue {
  sendViewAlertViewedEvent: (
    alertShown: AlertViewedPageData['alertShown'],
    paymentMethod: PaymentMethodType,
  ) => void;
  sendViewExperimentsEvent: (
    features: string[],
    country: Country,
    isCajaUpsell?: boolean,
    widget_displayed_data?: any,
  ) => void;
  sendLinkSelectPaymentMethodEvent: (
    paymentMethodSelected: PaymentMethodType,
  ) => void;
  sendLinkPostMyAdButtonEvent: (
    paymentMethod: PaymentMethodType,
    paymentMediumSelected?: string,
  ) => void;
  sendLinkPostCajaAdOrUpgradeButtonEvent: (
    paymentMethod: PaymentMethodType,
    paymentMediumSelected?: string,
  ) => void;
  sendLinkSaveCardCheckboxEvent: (toggleState: boolean) => void;
  sendLinkReplaceCardEvent: () => void;
  sendLinkDifferentPaymentMethodEvent: (toggleState: boolean) => void;
  sendLinkPaymentProcessingEvent: (
    paymentMethod: PaymentMethodType,
    paymentPendingDuration: number,
  ) => void;
  sendLinkCheckPaymentEvent: (paymentMethod: PaymentMethodType) => void;
  sendLinkDeleteCardButtonEvent: () => void;
  sendLinkSelectWayToPayEvent: (
    paymentMediumSelected: VirtualAccountBankAbbr,
    vaPaymentSelected: string,
  ) => void;
  sendLinkQRDisplayEvent: (paymentMethod: PaymentMethodType) => void;
}

// this function stays here so that we can enable and disable easily if needed, based on different use cases
const shouldPerformTracking = (
  useCase: UseCase,
  trackingAdapter?: TrackingAdapter,
) => {
  if (!trackingAdapter && useCase === 'CAJA') {
    return false;
  }
  return true;
};

let telium: TealiumAdapter | TrackingAdapter = adapter();

export const useAnalytics = () =>
  React.useContext<AnalyticsContextValue>(AnalyticsContext);

export const AnalyticsProvider = (props: any) => {
  const { children, trackingAdapter, useCase, invoice } = props;
  if (trackingAdapter) {
    telium = trackingAdapter as TrackingAdapter;
  }
  const analyticProxy = new Proxy(telium, {
    get(target, prop: keyof typeof telium) {
      // If it's not a function we will pass it through.
      if (typeof target[prop] !== 'function') {
        return Reflect.get(target, prop);
      }

      return (...fnArgs: any[]) => {
        if (!shouldPerformTracking(useCase, trackingAdapter)) return null;
        Reflect.apply(target[prop], target, fnArgs);
      };
    },
  }) as TealiumAdapter & TrackingAdapter;

  const sendViewAlertViewedEvent = (
    alertShown: AlertViewedPageData['alertShown'],
    paymentMethod: PaymentMethodType,
  ) => {
    const pageData: AlertViewedPageData = {
      eventName: 'alert_viewed',
      alertShown,
      paymentMethod,
    };

    if ((useCase as UseCase) === 'UPGRADE') {
      pageData.actionOrigin = 'upgrade';
    }

    if ((useCase as UseCase) === 'ADBUDGET') {
      pageData.actionOrigin = 'products discounts';
    }

    sendUtagLinkEvent(pageData.eventName, pageData);
  };

  const sendViewExperimentsEvent = (
    features: string[],
    country: Country,
    isCajaUpsell?: boolean,
    widget_displayed_data?: any,
  ) => {
    const experiments = getExperimentsFromFeatures(
      features,
      useCase,
      country,
      isCajaUpsell,
    );
    const pageData = {
      eventName: 'widget_displayed',
      widgetName: 'payment details',
      experiments,
      ...widget_displayed_data,
    };

    sendUtagLinkEvent(pageData.eventName, pageData);
  };

  const sendUtagLinkEvent = (eventName: string, data: any) => {
    if (trackingAdapter) {
      analyticProxy.sendLinkEvent(eventName, data);
    } else {
      analyticProxy.link(eventName, data);
    }
  };

  const sendLinkSelectPaymentMethodEvent = (
    paymentMethodSelected: PaymentMethodType,
  ) => {
    const linkData: {
      eventName: string;
      paymentMethodSelected: PaymentMethodType;
      invoiceId?: string[];
      actionOrigin?: string;
      linkName?: string;
    } = {
      eventName: 'select_payment_method_pressed',
      paymentMethodSelected,
    };

    if ((useCase as UseCase) === 'INVOICE') {
      linkData.invoiceId = (invoice as [Invoice]).map(({ id }) => id);
    }

    if ((useCase as UseCase) === 'UPGRADE') {
      linkData.linkName = 'select_payment_method_pressed';
      linkData.actionOrigin = 'upgrade';
    }

    if ((useCase as UseCase) === 'ADBUDGET') {
      linkData.linkName = 'select_payment_method_pressed';
      linkData.actionOrigin = 'products discounts';
    }
    sendUtagLinkEvent(linkData.eventName, linkData);
  };

  const sendLinkPostMyAdButtonEvent = (
    paymentMethod: PaymentMethodType,
    /**
     * Please fire this datalayer whenever hirers choose a payment method which allows them to select a preferred bank/ewallet/etc.
     *
     * for Banks: maybank2u | cimb | <bank name>
     *
     * for eWallets: ovo | dana | grabpay | <wallet name>
     */
    paymentMediumSelected?: string,
  ) => {
    const linkData: {
      eventName: string;
      paymentMethod: PaymentMethodType;
      invoiceId: string[];
      paymentMediumSelected?: string;
    } = {
      eventName: 'pay_invoice_pressed',
      paymentMethod,
      invoiceId: (invoice as [Invoice]).map(({ id }) => id),
    };

    if (paymentMediumSelected) {
      linkData.paymentMediumSelected = paymentMediumSelected;
    }

    sendUtagLinkEvent(linkData.eventName, linkData);
  };

  const sendLinkPostCajaAdOrUpgradeButtonEvent = (
    paymentMethod: PaymentMethodType,
    /**
     * Please fire this datalayer whenever hirers choose a payment method which allows them to select a preferred bank/ewallet/etc.
     *
     * for Banks: maybank2u | cimb | <bank name>
     *
     * for eWallets: ovo | dana | grabpay | <wallet name>
     */
    paymentMediumSelected?: string,
  ) => {
    const linkData: {
      eventName: string;
      paymentMethod: PaymentMethodType;
      paymentMediumSelected?: string;
      eventOrigin?: string;
      linkName?: string;
    } = {
      eventName: 'post_ad_pressed',
      paymentMethod,
    };

    if (paymentMediumSelected) {
      linkData.paymentMediumSelected = paymentMediumSelected;
    }

    if ((useCase as UseCase) === 'UPGRADE') {
      linkData.linkName = 'job_upgrade_checkout_pressed';
      linkData.eventName = 'job_upgrade_checkout_pressed';
      linkData.eventOrigin = 'upgrade';
    }

    if ((useCase as UseCase) === 'ADBUDGET') {
      linkData.linkName = 'checkout_pressed';
      linkData.eventName = 'checkout_pressed';
      linkData.eventOrigin = 'products discounts';
    }

    sendUtagLinkEvent(linkData.eventName, linkData);
  };

  const sendLinkSaveCardCheckboxEvent = (toggleState: boolean) => {
    const linkData: {
      eventName: string;
      toggleType: string;
      toggleState: string;
      actionOrigin?: string;
      linkName?: string;
    } = {
      eventName: 'toggle_pressed',
      toggleType: 'save_credit_card',
      toggleState: toggleState ? 'on' : 'off',
    };

    if ((useCase as UseCase) === 'UPGRADE') {
      linkData.linkName = 'toggle_pressed';
      linkData.actionOrigin = 'upgrade';
    }

    if ((useCase as UseCase) === 'ADBUDGET') {
      linkData.linkName = 'toggle_pressed';
      linkData.actionOrigin = 'products discounts';
    }
    sendUtagLinkEvent(linkData.eventName, linkData);
  };

  const sendLinkReplaceCardEvent = () => {
    const linkData = {
      eventName: 'replace_credit_card_pressed',
    };
    sendUtagLinkEvent(linkData.eventName, linkData);
  };

  /**
   * When hirer clicks on 'different payment method'
   */
  const sendLinkDifferentPaymentMethodEvent = () => {
    const linkData: any = {
      eventName: 'different_payment_method_pressed',
    };

    if ((useCase as UseCase) === 'UPGRADE') {
      linkData.linkName = 'different_payment_method_pressed';
      linkData.actionOrigin = 'upgrade';
    }

    sendUtagLinkEvent(linkData.eventName, linkData);
  };

  const sendLinkPaymentProcessingEvent = (
    paymentMethod: PaymentMethodType,
    paymentPendingDuration: number,
  ) => {
    const linkData: any = {
      eventName: 'payment_pending_displayed',
      paymentMethod,
      paymentPendingDuration,
    };

    if ((useCase as UseCase) === 'UPGRADE') {
      linkData.actionOrigin = 'upgrade';
    }

    if ((useCase as UseCase) === 'ADBUDGET') {
      linkData.actionOrigin = 'products discounts';
    }

    sendUtagLinkEvent(linkData.eventName, linkData);
  };

  const sendLinkCheckPaymentEvent = (paymentMethod: PaymentMethodType) => {
    const linkData: any = {
      eventName: 'check_payment_pressed',
      paymentMethod,
    };

    sendUtagLinkEvent(linkData.eventName, linkData);
  };

  const sendLinkDeleteCardButtonEvent = () => {
    const linkData: any = {
      eventName: 'delete_credit_card_pressed',
    };

    if ((useCase as UseCase) === 'UPGRADE') {
      linkData.linkName = 'delete_credit_card_pressed';
      linkData.actionOrigin = 'upgrade';
    }

    if ((useCase as UseCase) === 'ADBUDGET') {
      linkData.linkName = 'delete_credit_card_pressed';
      linkData.actionOrigin = 'products discounts';
    }

    sendUtagLinkEvent(linkData.eventName, linkData);
  };

  const sendLinkSelectWayToPayEvent = (
    paymentMediumSelected: string,
    vaPaymentSelected: string,
  ) => {
    const linkData = {
      eventName: 'select_ways_to_pay_pressed',
      paymentMediumSelected,
      vaPaymentSelected,
    };
    sendUtagLinkEvent(linkData.eventName, linkData);
  };

  const sendLinkQRDisplayEvent = (paymentMethod: PaymentMethodType) => {
    const linkData: {
      eventName: string;
      paymentMethod: PaymentMethodType;
      invoiceId?: string[];
    } = {
      eventName: 'purchase_qr_code_displayed',
      paymentMethod,
    };

    if ((useCase as UseCase) === 'INVOICE') {
      linkData.invoiceId = (invoice as [Invoice]).map(({ id }) => id);
    }

    sendUtagLinkEvent(linkData.eventName, linkData);
  };

  return (
    <AnalyticsContext.Provider
      value={{
        sendViewAlertViewedEvent,
        sendViewExperimentsEvent,
        sendLinkSelectPaymentMethodEvent,
        sendLinkPostMyAdButtonEvent,
        sendLinkPostCajaAdOrUpgradeButtonEvent,
        sendLinkSaveCardCheckboxEvent,
        sendLinkReplaceCardEvent,
        sendLinkDifferentPaymentMethodEvent,
        sendLinkPaymentProcessingEvent,
        sendLinkCheckPaymentEvent,
        sendLinkDeleteCardButtonEvent,
        sendLinkSelectWayToPayEvent,
        sendLinkQRDisplayEvent,
      }}
    >
      <>{children}</>
    </AnalyticsContext.Provider>
  );
};
